/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import {generateUUID} from '@dvrd/dvr-controls';
import {DefaultListParams, ModelResponse} from '../util/interfaces';
import {deepClone} from '../util/utils';
import {BaseFields, BaseModel, BaseProps, FieldDefinition, ModelFieldType} from './baseModel';

interface Props extends BaseProps {
    firstname?: string;
    lastname?: string | null;
    email?: string;
    group?: string | null;
    active?: boolean;
    index?: number;
}

export interface EmployeeFields extends BaseFields {
    firstname: string;
    lastname: string | null;
    email: string;
    group: string | null;
    active: boolean;
    index: number;
}

const fields: EmployeeFields = {
    id: '',
    created_at: '',
    updated_at: '',
    firstname: '',
    lastname: null,
    email: '',
    group: null,
    active: true,
    index: 0,
};

type FieldDefType = keyof EmployeeFields;

const fieldsDefinition: Record<FieldDefType, FieldDefinition> = {
    id: new FieldDefinition(ModelFieldType.STRING),
    created_at: new FieldDefinition(ModelFieldType.STRING),
    updated_at: new FieldDefinition(ModelFieldType.STRING, {allowNull: true, defaultValue: null}),
    firstname: new FieldDefinition(ModelFieldType.STRING),
    lastname: new FieldDefinition(ModelFieldType.STRING, {allowNull: true, defaultValue: null}),
    email: new FieldDefinition(ModelFieldType.STRING),
    group: new FieldDefinition(ModelFieldType.STRING, {allowNull: true, defaultValue: null}),
    active: new FieldDefinition(ModelFieldType.BOOLEAN),
    index: new FieldDefinition((ModelFieldType.NUMBER)),
};

const ignoreProps: Array<keyof EmployeeFields> = [];

type GetAllConfig = DefaultListParams & { active?: boolean; };

export default class Employee extends BaseModel<Props, EmployeeFields, FieldDefType> {
    static getAll = (config: GetAllConfig, callback: ModelResponse<Array<Employee>>) => {
        // TODO
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
        this.identity = props?.identity ?? `Employee.${props?.id ?? generateUUID()}`;
    }

    get firstname(): string {
        return this.fields.firstname;
    }

    set firstname(value: string) {
        this.fields.firstname = value;
    }

    get lastname(): string | null {
        return this.fields.lastname;
    }

    set lastname(value: string | null) {
        this.fields.lastname = value;
    }

    get email(): string {
        return this.fields.email;
    }

    set email(value: string) {
        this.fields.email = value;
    }

    get group(): string | null {
        return this.fields.group;
    }

    set group(value: string | null) {
        this.fields.group = value;
    }

    get active(): boolean {
        return this.fields.active;
    }

    set active(value: boolean) {
        this.fields.active = value;
    }

    get index(): number {
        return this.fields.index;
    }

    set index(value: number) {
        this.fields.index = value;
    }

    clone = (identityMap?: Map<string, any>): Employee => {
        const props = deepClone(this.fields, identityMap);
        return new Employee({...props, identity: this.identity});
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
    };
}